import React, { useEffect, useState } from "react";
import { api, money } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import StatCard from "../../components/common/StatCard";

export default function SellerDashboard() {
  const { seller } = useAuth();
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const [p, o] = await Promise.all([
          api.get("/products/seller/mine"),
          api.get("/orders/seller/mine"),
        ]);
        if (!alive) return;
        setProducts(p.data);
        setOrders(o.data);
      } catch (e) {
        if (alive) setError(e?.response?.data?.detail || "Failed to load dashboard");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, []);

  const revenue = orders.reduce((s, o) => s + (o.seller_subtotal || 0), 0);

  return (
    <div data-testid="seller-dashboard-page">
      <div className="overline mb-2">Welcome back</div>
      <h1 className="font-display text-4xl mb-2">{seller?.shop_name || "Your Studio"}</h1>
      <p className="text-sm text-[hsl(var(--kb-muted))] mb-8">
        Status: <b className={`uppercase ${seller?.status === "approved" ? "text-green-700" : "text-[hsl(var(--kb-primary))]"}`}>{seller?.status || "pending"}</b>
      </p>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10">
        <StatCard label="Products" value={loading ? "…" : products.length} testid="stat-products" />
        <StatCard label="Orders" value={loading ? "…" : orders.length} testid="stat-orders" />
        <StatCard label="Revenue" value={loading ? "…" : money(revenue)} testid="stat-revenue" />
        <StatCard label="Platform Fee" value="10%" testid="stat-fee" />
      </div>

      {error && <div className="mb-6 border border-red-300 bg-red-50 text-red-800 p-3 text-sm" data-testid="dashboard-error">{error}</div>}

      <div className="bg-white border border-[hsl(var(--kb-border))] p-6 rounded-sm">
        <div className="overline mb-4">Recent orders</div>
        {loading ? (
          <div className="text-sm text-[hsl(var(--kb-muted))]">Loading…</div>
        ) : orders.length === 0 ? (
          <div className="text-sm text-[hsl(var(--kb-muted))]">No orders yet.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-left text-[hsl(var(--kb-muted))] border-b border-[hsl(var(--kb-border))]">
                <tr><th className="py-2">Order</th><th>Items</th><th>Total</th><th>Status</th></tr>
              </thead>
              <tbody>
                {orders.slice(0, 5).map((o) => (
                  <tr key={o.id} className="border-b border-[hsl(var(--kb-border))]">
                    <td className="py-3 font-mono text-xs">#{o.id.slice(0, 8)}</td>
                    <td>{o.items.length}</td>
                    <td>{money(o.seller_subtotal)}</td>
                    <td className="uppercase text-xs">{o.status}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
